import * as React from 'react';
import { useState } from 'react';
import { animated, useChain, useSpring, useSpringRef, useTransition } from 'react-spring';
import { createUseStyles } from 'react-jss';

import { Category } from '../interfaces';
import { useMeasure } from '../helpers';
import { ArrowIcon } from './Icons';
import { useAppData } from '../providers';

const useStyles = createUseStyles({
    categoryList: {
        '& ol': {
            margin: 0
        }
    },
    category: {
        overflow: 'hidden',
        userSelect: 'none',
        fontSize: '1.5rem',
        width: '100%',
        listStyleType: 'none',
        backgroundColor: '#e6e6e6',
        '&:hover': {
            cursor: 'pointer',
            textDecoration: 'underline'
        },
        '& > span': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.5rem 2rem'
        }
    }
});

interface CategoryListProps {
    categories: Category[];
}

const CategoryList: React.FC<CategoryListProps> = ({ categories }: CategoryListProps) => {
    const classes = useStyles();
    const { selectCategory } = useAppData();
    const [refMap] = useState(() => new WeakMap());
    const springRef = useSpringRef();
    const transRef = useSpringRef();

    // @ts-ignore
    const [bind, { height: viewHeight }] = useMeasure();

    const categoryListAnimation = useSpring({
        ref: springRef,
        from: { opacity: 0, height: viewHeight },
        to: { opacity: 1, height: viewHeight }
    });

    const transitions = useTransition<Category, {}>(categories, {
        ref: transRef,
        from: item => async next => {
            await next({ opacity: 0, height: refMap.get(item).scrollHeight, transform: `translateY(20%)` });
        },
        enter: item => async next => {
            await next({ opacity: 1, height: refMap.get(item).scrollHeight, transform: `translateY(0)` });
        },
        leave: { opacity: 0, height: 0, transform: `translateY(-20%)` },
        config: { duration: 300 }
    });

    useChain([springRef, transRef], [0, 0.3], 0.5);

    return (
        <animated.div style={categoryListAnimation}>
            <nav className={classes.categoryList} {...bind}>
                {categories.length ? (
                    <ol>
                        {transitions((style, item) => (
                            <animated.li
                                key={item.identifier}
                                style={style}
                                data-tip={item.hint}
                                ref={ref => ref && refMap.set(item, ref)}
                                className={classes.category}
                                onClick={() => selectCategory(item)}
                            >
                                <span>
                                    {item.label}
                                    <ArrowIcon />
                                </span>
                            </animated.li>
                        ))}
                    </ol>
                ) : (
                    ''
                )}
            </nav>
        </animated.div>
    );
};

export default React.memo(CategoryList);
